import type { CostRecipe } from "./cost-recipe";
import type { QuantityDerivation } from "./quantity-dimensions";
import { steelSectionsFromEvidence } from "./section-supply-pricing";

const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

/** Compare a pricing axis with a named instance, never with another axis. */
export function matchesDimensionLabel(actual: string, expected: string): boolean {
  const label = norm(actual), key = norm(expected);
  if (label === key || label.endsWith(` ${key}`)) return true;
  // Steel designations can occur between the opening name and its axis.
  return key === "opening width" && /\b(?:opening|doorway)\b/.test(label) && /\bwidth$/.test(label)
    && !/\b(?:bearing|padstone|retained|maximum|minimum|limit)\b/.test(label);
}

/** A referenced support is not the work item being priced. */
export function isSteelMemberWork(data: QuantityDerivation | null | undefined, context = "",
  recipe?: CostRecipe | null, itemName?: string): boolean {
  const evidence = steelSectionsFromEvidence(data, context).length > 0
    || /\bsteel\s+(?:beam|column|post|lintel)\b/i.test(context)
    || /\b(?:UB|UC|RSJ|universal)\b.*\b(?:beam|column)\b/i.test(context)
    || !!(recipe?.shape === "specification" && recipe.supply_pricing?.section_rates);
  if (!evidence) return false;
  if (!itemName?.trim()) return true;
  const subject = itemName.split(/\b(?:for|over|below|under|above|beneath|supporting|supported|with|at)\b/i)[0];
  return /\b(?:steel|beam|column|post|rsj|goalpost)\b/i.test(subject)
    || /^\s*(?:(?:UB|UC)\s*)?\d{2,4}\s*[x×*]\s*\d{2,4}\s*[x×*]\s*\d+(?:\.\d+)?\s*(?:UB|UC)\b/i.test(subject);
}

/** Quote-local repair: retain the original input object and unrelated evidence. */
export function scopeWorkDimensions(data: QuantityDerivation | null | undefined, context = "",
  recipe?: CostRecipe | null, itemName?: string): QuantityDerivation | undefined {
  if (!data || !itemName?.trim() || isSteelMemberWork(data, context, recipe, itemName)) return data ?? undefined;
  const models = [recipe?.labour_pricing, recipe?.plant_pricing,
    recipe?.shape === "specification" ? recipe.supply_pricing : undefined,
    ...(recipe?.materials.flatMap(m => [m.dimension_pricing, m.quantity_pricing]) ?? [])];
  const required = models.flatMap(m => m ? m.dimension_labels : []);
  const dimensions = data.dimensions.filter(d => required.some(label => matchesDimensionLabel(d.label, label))
    || d.unit !== "kg/m" && !/\b(?:beam|steel|member)\s+(?:cut\s+)?(?:length|weight|size|section)\s*$/i.test(d.label));
  if (dimensions.length === data.dimensions.length) return data;
  const ids = new Set(dimensions.map(d => d.id));
  const broken = data.quantity_operands.some(id => !ids.has(id));
  return { ...data, dimensions, ...(broken ? { quantity_op: "direct" as const, quantity_operands: [] } : {}) };
}

/** Only shorten the visible heading; keep full labels and notes as source evidence. */
export function workDimensionHeading(label: string, steel: boolean): string {
  if (steel) return label;
  return label.replace(
    /\s*(?:[—–-]\s*)?(?:(?:below|under|over|with)\s+)?(?:(?:vertical\s+)?steel\s+beam\s+)?(?:UB\s*|UC\s*)?\d{2,4}\s*[x×*]\s*\d{2,4}\s*[x×*]\s*\d+(?:\.\d+)?\s*(?:UB|UC)\b(?:\s+beam)?/gi,
    "",
  ).replace(/\s*[—–-]\s*[—–-]\s*/g, " — ").replace(/\s+/g, " ").trim();
}
